import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Divider,
    FormControlLabel,
    InputAdornment,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import {
    AddLinkOutlined,
    Handshake,
    Link,
    Loop,
    TextFieldsOutlined,
} from "@mui/icons-material";

import Vendors from "../Vendors";
import CustomizedSnackbars from "../CustomizedSnackbars";
import paymentlinkservice from "../../../../services/paymentlinkservice";
import Swal from "sweetalert2";
import Percentage from "../Percentage";
import portalpaymentlink from "../../../../services/portalpaymentlink";
import { matchXOrLinkedinUrl } from "./socialUrl";

// ---------------------------------------------------------------------------
// Zod Validation Schema
// ---------------------------------------------------------------------------
// Each field is validated individually. We use `.refine()` for custom rules
// like numeric-only totalAmount.
// ---------------------------------------------------------------------------
const PaymentFormSchema = z.object({
    /** Internal campaign ID (hidden / disabled field) */
    campaign: z
        .string({
            required_error: "Campaign is required",
            invalid_type_error: "Campaign must be a string",
        })
        .min(3, { message: "Not a valid Campaign ID" }),

    /** Public-facing campaign name — set from props, not submitted */
    campaignName: z.string().optional(),

    /** Social media profile URL — must be a proper URL */
    instagramLink: z
        .string({
            required_error: "Social Media Link is required",
            invalid_type_error: "Social Media Link must be a string",
        })
        .url({ message: "Must be a valid URL" })
        .min(3, { message: "Not a valid Social Media Link" }),

    /** Resolved username after URL detection */
    username: z
        .string({
            required_error: "Username is required",
            invalid_type_error: "Username must be a string",
        })
        .min(3, { message: "Not a valid username" }),

    /** Vendor selection — nullable because it is optional */
    vendor: z.nullable(z.string()),

    /** Free-text comment for internal notes */
    comment: z.string(),

    /** Deliverables description for the influencer */
    deliverables: z
        .string({ required_error: "Deliverables are required" })
        .min(1, { message: "Deliverables field cannot be empty" }),

    /** Base cost in INR — digits only */
    totalAmount: z.coerce.number().refine(
        (val) => /^[0-9]+$/.test(val),
        { message: "Amount must be a positive whole number" }
    ),

    /** Whether GST should be applied */
    includeGST: z.boolean(),

    /** Computed GST amount — null when GST is not included */
    gstAmount: z.nullable(z.string()),

    /** Final amount after applying percentage split */
    payableAmount: z.string(),

    /** Influencer MongoDB ObjectId */
    influencer: z.string(),

    /** Payment split percentage (1–100) */
    percentage: z.number({ required_error: "Please select a percentage" }),

    /**
     * Post links created for this campaign.
     * Stored as an array of URL strings; validated individually when added.
     */
    postLinks: z.array(z.string().url()).optional(),
});

// ---------------------------------------------------------------------------
// Helpers — URL Detection
// ---------------------------------------------------------------------------

/**
 * Checks whether a given URL matches a known social-media platform pattern.
 *
 * Returns [platformName, username] on success, or [false] if no match.
 *
 * Supported platforms: instagram, youtube, telegram/twitter, facebook.
 *
 * NOTE: The original code had a stray `https:` label on the for-of loop which
 * made it a labelled statement — harmless at runtime but misleading. Removed.
 */
const isValidSocialMediaUrl = (path) => {
    const url = path.toLowerCase();
    const social = matchXOrLinkedinUrl(path.trim());
    if (social) return social;

    const regexes = {
        instagram: /^https?:\/\/(?:www\.)?instagram\.com\/([a-zA-Z0-9_.]+)\/?$/,
        youtube: /^https?:\/\/(?:www\.)?youtube\.com\/([a-zA-Z0-9.@_]+)\/?$/,
        // Covers both t.me / telegram.me and twitter.com
        telegram: /^(https?:\/\/(?:www\.)?(?:t\.me|telegram\.me)\/([a-zA-Z0-9_@.]+)\/?|https?:\/\/twitter\.com\/([a-zA-Z0-9_]+)\/?)$/,
        facebook: /^https?:\/\/(?:www\.)?facebook\.com\/([a-zA-Z0-9._@]+)\/?$/,
    };

    for (const [platform, regex] of Object.entries(regexes)) {
        const match = url.match(regex);
        if (match) {
            // match[1] is the first capture group — the username segment
            return [platform, match[1]];
        }
    }

    return [false];
};

/**
 * Computes the payable amount from a base amount and a split percentage.
 * Returns a rounded integer as a string, or "0" on invalid input.
 */
const computePayable = (totalAmount, percentage) => {
    const base = parseInt(totalAmount, 10);
    const pct = parseInt(percentage, 10);
    if (isNaN(base) || isNaN(pct)) return "0";
    return String(Math.round((base * pct) / 100));
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * InfluencerForm
 *
 * Renders a payment-link creation form for influencer campaigns.
 *
 * Props:
 *   campaignDetails  — { id, name }  current campaign context
 *   vendors          — array of vendor objects passed down to <Vendors />
 *   styles           — MUI sx prop forwarded to the outer <Card />
 */
export default function InfluencerPaymentForm({ campaignDetails, vendors, styles }) {
    // ── URL detection loading state ──────────────────────────────────────────
    const [urlLoading, setURLLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ── Post-links chip state ────────────────────────────────────────────────
    // Holds the array of post-link URLs displayed as chips.
    const [postLinks, setPostLinks] = useState([]);
    // Controlled input value for the "add post link" text field.
    const [postLinkInput, setPostLinkInput] = useState("");
    // Validation error message for the post-link input.
    const [postLinkError, setPostLinkError] = useState("");

    // ── Snackbar state ───────────────────────────────────────────────────────
    const [openSnackBar, setSnackBar] = useState(false);
    const [snackBarSeverity, setSnackBarSeverity] = useState("success");
    const [snackBarMessage, setSnackBarMessage] = useState(
        "Payment Link has been created successfully"
    );

    // ── React Hook Form setup ────────────────────────────────────────────────
    const {
        register,
        handleSubmit,
        setValue,
        getValues,
        control,
        formState: { errors },
        reset,
        setError,
        clearErrors
    } = useForm({ resolver: zodResolver(PaymentFormSchema) });

    // Watch totalAmount and percentage so we can reactively update payableAmount
    const watchedTotal = useWatch({ control, name: "totalAmount" });
    const watchedPercentage = useWatch({ control, name: "percentage" });
    const watchGstToggle = useWatch({ control, name: "includeGST" });

    const gstAmount = useMemo(
        () => Math.round(Number(watchedTotal || 0) * 0.18),
        [watchedTotal]
    )

    // ── Reactive payable amount calculation ──────────────────────────────────
    // FIX: Previously payableAmount was hardcoded to 0.
    // Now it recomputes whenever totalAmount or percentage changes.
    useEffect(() => {
        const payable = computePayable(watchedTotal, watchedPercentage);
        if (Number(payable) > Number(campaignDetails.paymentLimit)) {
            setError(
                "payableAmount",
                {
                    type: 'manual',
                    message: 'Payable amount exceeds available campaign limit'
                }
            );
            return;
        }

        clearErrors("payableAmount");
        setValue("payableAmount", payable);
    }, [watchedTotal, watchedPercentage, setValue, setError, clearErrors, campaignDetails]);

    // ── Seed default form values on mount ────────────────────────────────────
    useEffect(() => {
        setValue("campaign", campaignDetails.id);
        setValue("campaignName", campaignDetails.name);
        setValue("percentage", 100);
        setValue("gstAmount", null);
        setValue("includeGST", false);
        setValue("payableAmount", "0");
        setValue("postLinks", []);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Snackbar helpers ─────────────────────────────────────────────────────
    const handleSnackBarClose = () => setSnackBar(false);

    const handleSnackBarOpen = (severity, message) => {
        setSnackBarSeverity(severity || "success");
        setSnackBarMessage(message || "Payment Link has been created successfully");
        setSnackBar(true);
    };

    // ── Form submission ──────────────────────────────────────────────────────
    /**
     * onSubmit — called by RHF after schema validation passes.
     * Injects the current postLinks array into formData before sending.
     */
    const onSubmit = async (formData, event) => {
        try {
            setIsSubmitting(true);
            const action = event?.nativeEvent?.submitter?.value;
            // Attach accumulated post links to the payload
            let finalLinks = [...postLinks];
            if (postLinkInput.trim()) {
                finalLinks.push(postLinkInput);
            }

            const payload = {
                ...formData,
                postLinks,
                gstAmount: gstAmount || 0
            };

            const generateLinkResp = await portalpaymentlink.generatePaymentLinkForCreator(payload);
            console.log(`generate link resp: `, generateLinkResp);

            if (generateLinkResp['data']?.['success']) {
                Swal.fire({
                    title: 'Influencer Payment Link',
                    text: 'Payment Link has been generated',
                    icon: 'success',
                    showConfirmButton: true,
                    didClose: () => {
                        if (action === "reload") {
                            window.location.reload();
                        }
                        else if (action === "redirect") {
                            window.location.replace('/payments/status');
                        }
                    },
                });
            }
            else {
                Swal.fire({
                    title: 'Link error',
                    text: 'Payment Link cannot be generated',
                    showConfirmButton: true,
                    icon: 'error',
                    didClose: () => { window.location.reload() }
                });
            }
        }
        catch (error) {
            let errorMessage = error?.response?.data?.message || error?.message;
            Swal.fire({
                title: 'Link error',
                text: errorMessage,
                showConfirmButton: true,
                icon: 'error'
            });
        }
        finally {
            setIsSubmitting(false);
        }
    };

    // ── Social-media URL detection ───────────────────────────────────────────
    /**
     * handleDetectUrl
     *
     * Fires on every change of the Social Media Link input.
     * 1. Validates the URL against known platform patterns.
     * 2. Fetches influencer details from the backend.
     * 3. Auto-fills deliverables, totalAmount, username, etc.
     *
     * FIX: urlLoading was not reset to false on the invalid-URL early-return
     * path. Added setURLLoading(false) before that return.
     */
    const handleDetectUrl = async (event) => {
        setURLLoading(true);
        const urlString = event.target.value;

        if (!urlString) {
            setURLLoading(false); // nothing to do — clear loading indicator
            return;
        }

        let parsedUrl;
        try {
            parsedUrl = new URL(urlString);
        } catch {
            // Not a valid URL yet — wait for more input
            setURLLoading(false);
            return;
        }

        const isValidUrl = isValidSocialMediaUrl(urlString);

        if (!isValidUrl[0]) {
            // FIX: was missing setURLLoading(false) here — loader would spin forever
            setURLLoading(false);
            Swal.fire({
                icon: "error",
                title: "Oops…",
                text: `Not a valid ${parsedUrl.hostname} link`,
            });
            setValue("instagramLink", "");
            return;
        }

        // Destructure the detected platform and username
        const [platform, username] = isValidUrl;
        setValue("platform", platform);

        try {
            // const resp = await socialmediaservice.UserDetails({
            //     url: urlString,
            //     campaignId: getValues("campaign"),
            // });

            const resp = await portalpaymentlink.verifyProfileLinkForPayment({
                url: urlString,
                campaignId: getValues("campaign")
            });

            console.log(`resp: `, resp);

            // 201 from this service signals a "not found / conflict" condition
            if (resp.status === 201) {
                Swal.fire({
                    icon: "error",
                    title: "Oops…",
                    text: resp.data,
                    timer: 3000,
                });
                setURLLoading(false);
                return;
            }

            // Populate fields with data returned from the backend
            const respData = resp.data;
            if (respData) {
                setValue("remainingPercentage", respData["percentage"]);
                setValue("percentage", respData["percentage"]);
                setValue("totalAmount", respData["totalCost"]);
                setValue("deliverables", respData["deliverables"]);
                setValue("username", respData["username"]);
                setValue("influencer", respData["_id"]);
                setValue("includeGST", respData["isGstPaid"]);
            }
        } catch (err) {
            // X / LinkedIn lookups answer with the real reason (not found, SocialCrawl credits...).
            const reason = err?.response?.data;
            handleSnackBarOpen("error", typeof reason === "string" && reason ? reason : "Failed to fetch influencer details");
        } finally {
            setURLLoading(false);
        }
    };

    // ── Post-link chip management ────────────────────────────────────────────

    /** Validates and adds a URL to the postLinks chip list. */
    const handleAddPostLink = () => {
        const trimmed = postLinkInput.trim();
        if (!trimmed) {
            setPostLinkError("Please enter a URL");
            return;
        }
        try {
            new URL(trimmed); // throws if invalid
        } catch {
            setPostLinkError("Must be a valid URL (include https://)");
            return;
        }
        if (postLinks.includes(trimmed)) {
            setPostLinkError("This link has already been added");
            return;
        }
        const updated = [...postLinks, trimmed];
        setPostLinks(updated);
        setValue("postLinks", updated);
        setPostLinkInput("");
        setPostLinkError("");
    };

    /** Removes a post-link chip by index. */
    const handleDeletePostLink = (index) => {
        const updated = postLinks.filter((_, i) => i !== index);
        setPostLinks(updated);
        setValue("postLinks", updated);
    };

    /** Allow pressing Enter to add a chip without clicking the button. */
    const handlePostLinkKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddPostLink();
        }
    };

    // ── Derived state ────────────────────────────────────────────────────────
    const hasErrors = Object.keys(errors).length > 0;
    // Display value for payable amount (mirrors the watched computation)
    const payableDisplay = computePayable(watchedTotal, watchedPercentage);

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* ── Global snackbar notification ── */}
            <CustomizedSnackbars
                handleSnackBarClose={handleSnackBarClose}
                open={openSnackBar}
                severity={snackBarSeverity}
                message={snackBarMessage}
            />

            <Card sx={{ ...styles, borderRadius: 3, boxShadow: 4 }}>
                {
                    isSubmitting &&
                    <Stack
                        padding={2}
                    >
                        <Alert
                            severity="info"
                        >
                            <AlertTitle>
                                <strong>Creating Payment Link</strong>
                            </AlertTitle>

                            Payment Link is being created...
                        </Alert>
                    </Stack>
                }

                <CardContent sx={{ p: 3 }}>
                    {/* ── Form title ── */}
                    <Typography
                        variant="h6"
                        fontWeight={700}
                        mb={2}
                        sx={{ letterSpacing: 0.5 }}
                    >
                        Influencer Payment Details
                    </Typography>

                    <Stack spacing={3}>

                        {/* ════════════════════════════════════════════════════════════
                            Section 1 — Campaign & Social Media Link
                        ════════════════════════════════════════════════════════════ */}
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            {/* Campaign (read-only — value comes from parent props) */}
                            <TextField
                                size="medium"
                                label="Campaign *"
                                value={campaignDetails.name}
                                variant="outlined"
                                sx={{ width: { xs: "100%", sm: "50%" } }}
                                {...register("campaign")}
                                disabled
                                InputLabelProps={{ shrink: true }}
                                InputProps={{ endAdornment: <Handshake color="action" /> }}
                                helperText={errors.campaign?.message}
                                error={!!errors.campaign}
                            />

                            {/* Social Media Link — triggers platform detection on change */}
                            <TextField
                                size="medium"
                                label="Social Media Link *"
                                placeholder="Profile link, e.g. instagram.com/username, x.com/handle or linkedin.com/in/name"
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    endAdornment: urlLoading
                                        ? (
                                            <Tooltip title="Fetching influencer data…">
                                                <Loop
                                                    color="primary"
                                                    sx={{
                                                        animation: "spin 1s linear infinite",
                                                        "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
                                                    }}
                                                />
                                            </Tooltip>
                                        )
                                        : <Link color="action" />,
                                }}
                                variant="outlined"
                                sx={{ width: { xs: "100%", sm: "50%" } }}
                                {...register("instagramLink")}
                                onChange={handleDetectUrl}
                                helperText={errors.instagramLink?.message}
                                error={!!errors.instagramLink}
                            />
                        </Stack>

                        {/* ════════════════════════════════════════════════════════════
                            Section 2 — Deliverables (left) + Vendor / Comment / Amounts (right)
                        ════════════════════════════════════════════════════════════ */}
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            {/* Deliverables — multi-line description of what the influencer will create */}
                            <TextField
                                label="Deliverables *"
                                InputLabelProps={{ shrink: true }}
                                placeholder="Describe reels, posts, stories, etc."
                                multiline
                                rows={7}
                                sx={{ width: { xs: "100%", sm: "50%" } }}
                                {...register("deliverables")}
                                helperText={errors.deliverables?.message}
                                error={!!errors.deliverables}
                            />

                            {/* Right column — vendor, comment, amounts */}
                            <Stack
                                direction="column"
                                spacing={2}
                                sx={{ width: { xs: "100%", sm: "50%" } }}
                            >
                                {/* Vendor selector — delegates to <Vendors /> sub-component */}
                                <Vendors
                                    data={vendors}
                                    setValue={setValue}
                                    getValues={getValues}
                                    control={control}
                                />

                                {/* Internal comment — optional, for future reference */}
                                <TextField
                                    size="medium"
                                    label="Comment (If Applicable)"
                                    variant="outlined"
                                    multiline
                                    rows={1}
                                    {...register("comment")}
                                    placeholder="Notes for future reference"
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        endAdornment: <TextFieldsOutlined color="action" />,
                                    }}
                                    helperText={errors.comment?.message}
                                    error={!!errors.comment}
                                />

                                {/* ── Amount row: Total / Payable / Percentage ── */}
                                <Stack direction="row" spacing={2} alignItems="flex-start">
                                    {/* Total negotiated amount (INR, digits only) */}
                                    <TextField
                                        size="medium"
                                        label="Total Amount *"
                                        variant="outlined"
                                        sx={{ width: "40%" }}
                                        InputLabelProps={{ shrink: true }}
                                        placeholder="0"
                                        {...register("totalAmount")}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">₹</InputAdornment>
                                            ),
                                        }}
                                        helperText={errors.totalAmount?.message || `Remaining Budget: ${campaignDetails.paymentLimit}`}
                                        error={!!errors.totalAmount}
                                        type="number"
                                    />

                                    {/* Payable amount — auto-computed, read-only */}
                                    {/* FIX: was hardcoded to 0; now derived from totalAmount × percentage */}
                                    <TextField
                                        size="medium"
                                        disabled
                                        label="Payable Amount"
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        value={payableDisplay}
                                        sx={{ width: "30%" }}
                                        {...register("payableAmount")}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">₹</InputAdornment>
                                            ),
                                        }}
                                    />

                                    {/* Percentage selector — delegates to <Percentage /> sub-component */}
                                    <Percentage
                                        setValue={setValue}
                                        getValues={getValues}
                                        control={control}
                                        maxPercentage={getValues('remainingPercentage') || 100}
                                    />
                                </Stack>
                            </Stack>
                        </Stack>

                        <Stack>
                            {/* isGst and gstAmount */}
                            <Controller
                                name="includeGST"
                                control={control}
                                render={({ field }) => (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                {...field}
                                                checked={!!field.value}
                                                onChange={(e) => field.onChange(e.target.checked)}
                                            />
                                        }

                                        label={
                                            <Typography
                                                fontSize={14}
                                            >
                                                {`Include GST (₹${gstAmount})`}
                                            </Typography>
                                        }
                                    />
                                )}
                            />
                        </Stack>

                        {/* ════════════════════════════════════════════════════════════
                            Section 3 — Post Links (chip input)
                            Allows attaching multiple published post URLs to this record.
                        ════════════════════════════════════════════════════════════ */}
                        <Box>
                            <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                mb={1}
                                color="text.secondary"
                                sx={{ letterSpacing: 0.3 }}
                            >
                                Post Links
                            </Typography>

                            {/* Input row — text field + Add button */}
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                <TextField
                                    size="small"
                                    label="Add Post Link"
                                    placeholder="Post link (Instagram, YouTube, X or LinkedIn)"
                                    variant="outlined"
                                    fullWidth
                                    value={postLinkInput}
                                    onChange={(e) => {
                                        setPostLinkInput(e.target.value);
                                        if (postLinkError) setPostLinkError(""); // clear error on typing
                                    }}
                                    onKeyDown={handlePostLinkKeyDown}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        endAdornment: <AddLinkOutlined color="action" fontSize="small" />,
                                    }}
                                    helperText={postLinkError || "Paste link and press Enter or it will be added automatically"}
                                    error={!!postLinkError}
                                    onBlur={() => {
                                        if (postLinkInput.trim()) {
                                            handleAddPostLink();
                                        }
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleAddPostLink}
                                    sx={{ mt: 0.5, minWidth: 48, height: 40 }}
                                >
                                    +
                                </Button>
                            </Stack>

                            {/* Chip list — renders one chip per added post link */}
                            {postLinks.length > 0 && (
                                <Box
                                    sx={{
                                        mt: 1.5,
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 1,
                                        p: 1.5,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 2,
                                        backgroundColor: "action.hover",
                                    }}
                                >
                                    {postLinks.map((link, index) => (
                                        <Tooltip key={index} title={link} placement="top" arrow>
                                            <Chip
                                                label={
                                                    // Truncate long URLs for display clarity
                                                    link.length > 45 ? `${link.slice(0, 42)}…` : link
                                                }
                                                onDelete={() => handleDeletePostLink(index)}
                                                color="primary"
                                                variant="outlined"
                                                size="small"
                                                sx={{ maxWidth: 260 }}
                                                component="a"
                                                href={link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                clickable
                                            />
                                        </Tooltip>
                                    ))}
                                </Box>
                            )}
                        </Box>

                        <Divider />

                        {/* ════════════════════════════════════════════════════════════
                            Section 4 — Action Buttons
                            Three submit variants:
                            • Add Another — submit and immediately clear for next entry
                            • Submit       — standard submission
                            • Submit with GST — submission that includes GST calculation
                        ════════════════════════════════════════════════════════════ */}
                        <Box sx={{ textAlign: "center" }}>
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                                justifyContent="center"
                            >
                                {/* Add Another — disabled when there are validation errors */}
                                <Button
                                    type="submit"
                                    disabled={hasErrors || isSubmitting}
                                    variant="outlined"
                                    color="info"
                                    sx={{ minWidth: 140, borderRadius: 2 }}
                                    value={'reload'}
                                >
                                    Add Another
                                </Button>

                                {/* Standard submit */}
                                <Button
                                    type="submit"
                                    color="success"
                                    variant="contained"
                                    disabled={hasErrors || isSubmitting}
                                    sx={{ minWidth: 140, borderRadius: 2 }}
                                    value={'redirect'}
                                >
                                    Submit
                                </Button>

                                {/* GST submit — triggers GST calculation path on the backend */}
                                {/* <Button
                                    type="submit"
                                    color="warning"
                                    variant="contained"
                                    sx={{ minWidth: 160, borderRadius: 2 }}
                                >
                                    Submit with GST
                                </Button> */}
                            </Stack>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
        </form>
    );
}